import { isEmpty, uniqBy } from "lodash";
import React from "react";
import Draggable from "react-draggable";
import { useSelector } from "react-redux";
import FilterIcon from "../../assets/img/dashboard/filter-butn.svg";
import moment from "moment";
import { formatCapitalRequestSourceValue, getCapitalRequestLabel } from "../../utils/capitalRequestLabel";
import {
    ASSET_LIFECYCLE_FILTER_NAME,
    formatDashboardFilterOptionName,
    formatDashboardFilterOptionNames,
    getAssetLifecycleFilterLabels,
    getDashboardFilterFieldLabel
} from "../../utils/dashboardFilterLabel";
import { fieldMatchesFilterWildcardKey } from "../../utils/filtersUtils";
import { formatAppliedWildcardFilterDisplayValues } from "../../utils/wildCardFilterUtils";
import { formatProjectTypesLabel, isAdcOnlyProjectTypes } from "../project/projectTypes";

export const AppliedFilters = ({
    onCancel,
    myFilters,
    openFiltersPopup,
    showFiltersIcon,
    filterValues,
    tableConfig,
    source,
    MyfilterName,
    localQueries = [],
    isCR = false,
    assetDashboardFilterTags = [],
    extraFilterTags = [],
    extraFilterTagsHeading = "",
    extraFilterTagsAsSection = false
}) => {
    const { filterContents = {}, masterFilterList, filterFields, dashboardExtraFilters: queries } = useSelector(state => state.dashboardReducer);
    const miscellaneous = useSelector(state => state.projectReducer?.getProjectByIdResponse?.miscellaneous);
    const projectTypes = useSelector(state => state.projectReducer?.getProjectByIdResponse?.project_types);
    const isAdcOnlyProject = isAdcOnlyProjectTypes(projectTypes);
    const capitalRequestLabel = getCapitalRequestLabel(miscellaneous);
    const getCspStatusLabels = values =>
        (Array.isArray(values) ? values : [])
            .filter(value => value !== "recommendation" || values.includes("capital_request"))
            .map(value =>
                value === "capital_request" ? capitalRequestLabel : value === "recommendation" ? "Recommendation" : value
            );
    const resolveFilterDisplayName = name => {
        if (!name || name === "exclude_filters") return name;
        const excludeSuffix = name.endsWith("-Exclude") ? "-Exclude" : "";
        const baseName = excludeSuffix ? name.slice(0, -excludeSuffix.length) : name;
        const field = filterFields?.find(fi => fi.label === baseName);
        if (field) {
            return `${getDashboardFilterFieldLabel(field, miscellaneous, isCR)}${excludeSuffix}`;
        }
        return name;
    };
    const getAppliedFiltersList = filters => {
        let filterNames = [];
        filters &&
            Object.entries(filters).forEach(([key, value]) => {
                uniqBy(filterFields, "key").forEach(fi => {
                    if (key === "filters" && !isEmpty(filters[key])) {
                        Object.entries(value).forEach(([wKey, wValue]) => {
                            if (fieldMatchesFilterWildcardKey(fi, wKey)) {
                                filterNames.push({
                                    name: getDashboardFilterFieldLabel(fi, miscellaneous, isCR),
                                    value: formatAppliedWildcardFilterDisplayValues(wValue, fi.fieldType, fi)
                                });
                            }
                        });
                    } else if (key === "top_n" && !isEmpty(filters[key])) {
                        Object.entries(value).forEach(([wKey, wValue]) => {
                            if (fieldMatchesFilterWildcardKey(fi, wKey)) {
                                filterNames.push({
                                    name: getDashboardFilterFieldLabel(fi, miscellaneous, isCR),
                                    value: [`${wValue || ""} [Top N]`]
                                });
                            }
                        });
                    } else if (fi.paramKey === key) {
                        if (key === "csp_status") {
                            const names = getCspStatusLabels(value);
                            if (names.length) {
                                filterNames.push({
                                    name: getDashboardFilterFieldLabel(fi, miscellaneous, isCR),
                                    value: names
                                });
                            }
                            return;
                        }
                        if (key === "recommendation_type" && isAdcOnlyProject) {
                            const names = getAssetLifecycleFilterLabels(value);
                            if (names.length) {
                                filterNames.push({
                                    name: ASSET_LIFECYCLE_FILTER_NAME,
                                    value: names
                                });
                            }
                            return;
                        }
                        const names =
                            masterFilterList?.[fi.key]?.filter(ms => value.includes(ms.id))?.map(item =>
                                formatDashboardFilterOptionName(item.name, fi.key, miscellaneous)
                            ) || [];
                        filterNames.push({
                            name: getDashboardFilterFieldLabel(fi, miscellaneous, isCR),
                            value:
                                key === "building_types" ? formatDashboardFilterOptionNames(value, fi.key, miscellaneous) : names
                        });
                    }
                });
            });
        filters?.exclude_filters &&
            Object.entries(filters?.exclude_filters).forEach(([key, value]) => {
                uniqBy(filterFields, "key").forEach(fi => {
                    if (fi.paramKey === key) {
                        const names =
                            masterFilterList?.[fi.key]?.filter(ms => value.includes(ms.id))?.map(item =>
                                formatDashboardFilterOptionName(item.name, fi.key, miscellaneous)
                            ) || [];
                        filterNames.push({
                            name: `${getDashboardFilterFieldLabel(fi, miscellaneous, isCR)}-Exclude`,
                            value:
                                key === "building_types" ? formatDashboardFilterOptionNames(value, fi.key, miscellaneous) : names
                        });
                    }
                });
            });
        if (filters?.start_year && filters?.end_year) {
            filterNames.push({
                name: "Years",
                value: { start: filters?.start_year, end: filters?.end_year }
            });
        }
        if ("update_fci" in filters && filters.update_fci === false) {
            filterNames.push({
                name: "Static FCI",
                value: ["No"]
            });
        }
        if (filters.project_cost) {
            filterNames.push({
                name: "Project Cost",
                value: ["Yes"]
            });
        }
        return filterNames;
    };
    const FilterNameFormat = data => {
        const formattedData = [];
        for (const key in data) {
            formattedData.push({ name: resolveFilterDisplayName(key), value: data[key] });
        }
        return formattedData;
    };

    const formatDashboardTagValue = (filterLabel, value) => {
        const fi = filterFields?.find(f => f.label === filterLabel || `${f.label}-Exclude` === filterLabel);
        return formatDashboardFilterOptionName(value, fi?.key, miscellaneous);
    };

    const renderFilters = filters => {
        return (
            <>
                {filters &&
                    Object.values(filters).map(f => {
                        return f.name !== "Years" ? (
                            f?.value?.length > 0 && (
                                <>
                                    <span className="tag active">{f.name} </span>
                                    {f.value.map(item => {
                                        const display =
                                            source === "dashboard" &&
                                            (typeof item === "string" || typeof item === "boolean")
                                                ? formatDashboardTagValue(f.name, item)
                                                : item;
                                        return (
                                            <span key={item} className="tag">
                                                {display}
                                            </span>
                                        );
                                    })}
                                    <br />
                                </>
                            )
                        ) : (
                            <>
                                <span className="tag active">{f.name} </span>
                                <span className="tag">Start : {f.value.start}</span>
                                <span className="tag">End : {f.value.end}</span>
                            </>
                        );
                    })}
            </>
        );
    };

    const formatListDisplayValue = (fi, r, objectKey) => {
        const fieldConfig =
            tableConfig &&
            Object.values(tableConfig).find(
                field => (field.commonSearchKey === fi || field.searchKey === fi) && (!objectKey || field.commonSearchObjectKey === objectKey)
            );
        let display = fieldConfig?.formatFilterLabel ? fieldConfig.formatFilterLabel(r) : r;
        if (fi === "source" || objectKey === "source") {
            display = formatCapitalRequestSourceValue(r, miscellaneous);
        }
        return display === true ? "Yes" : display === false ? "No" : display;
    };

    const renderListFilter = values => {
        return Object.keys(values).map(fi => {
            let label = "";
            if (tableConfig && Object.keys(tableConfig)?.length) {
                label =
                    Object.values(tableConfig || {}).find(
                        field => field.searchKey === fi || field.commonSearchKey === fi
                    )?.label || "";
            }
            label = label ? label : fi;

            if (fi === "source" && values[fi]?.source && Array.isArray(values[fi].source)) {
                return values[fi].source.map(r => (
                    <span className="filter" key={r}>
                        {label} : {formatListDisplayValue(fi, r)}
                    </span>
                ));
            }
            if (values[fi] && values[fi].length) {
                return values[fi].map(r => {
                    return (
                        <span className="filter">
                            {label} : {formatListDisplayValue(fi, r)}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].name) {
                return values[fi].name.map(r => {
                    return (
                        <span className="filter">
                            {label} : {formatListDisplayValue(fi, r)}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].project_types) {
                const typeLabel =
                    Object.values(tableConfig || {}).find(field => field.commonSearchObjectKey === "project_types")?.label || "Project Type";
                return values[fi].project_types.map(r => (
                    <span className="filter" key={r}>
                        {typeLabel} : {formatProjectTypesLabel(r)}
                    </span>
                ));
            } else if (values[fi] && values[fi].year) {
                return values[fi].year.map(r => {
                    return <span className="filter">year : {r}</span>;
                });
            } else if (values[fi] && values[fi].month) {
                return values[fi].month.map(r => {
                    return <span className="filter">month : {r}</span>;
                });
            } else if (values[fi] && values[fi].account_type) {
                return values[fi].account_type.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].meter) {
                return values[fi].meter.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].meter_type) {
                return values[fi].meter_type.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].hospital_name) {
                return values[fi].hospital_name.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].file_type) {
                return values[fi].file_type.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].description) {
                return values[fi].description.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].file_name) {
                return values[fi].file_name.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            } else if (values[fi] && values[fi].document_type) {
                return values[fi].document_type.map(r => {
                    return (
                        <span className="filter">
                            {fi} : {r}
                        </span>
                    );
                });
            }
        });
    };

    /**
     * Asset table only: each local list filter reads as a field pill followed by its values, matching the
     * Dashboard Filter section above it, instead of sitting under one shared "Common filter" badge that
     * says nothing about which field the values belong to. Every other table keeps renderListFilter as it
     * was. Shapes this does not recognise fall back to that shared renderer so no applied filter is lost.
     */
    const renderAssetListFilter = values =>
        Object.keys(values).map(fi => {
            const label =
                (tableConfig && Object.keys(tableConfig)?.length
                    ? Object.values(tableConfig).find(field => field.searchKey === fi || field.commonSearchKey === fi)?.label
                    : "") || fi;
            const items = Array.isArray(values[fi]) ? values[fi] : Array.isArray(values[fi]?.name) ? values[fi].name : null;
            if (!items) return renderListFilter({ [fi]: values[fi] });
            return items.length ? (
                <React.Fragment key={fi}>
                    <span className="tag active">{label} </span>
                    {items.map((r, index) => (
                        <span className="tag" key={`${fi}-${index}`}>
                            {formatListDisplayValue(fi, r)}
                        </span>
                    ))}
                    <br />
                </React.Fragment>
            ) : null;
        });

    const renderWildCardFilter = values => {
        return Object.keys(values).map(fi => {
            let label = "";
            let type = "";
            if (tableConfig && Object.keys(tableConfig)?.length) {
                const config = Object.values(tableConfig || {}).find(field => field.searchKey === fi) || {};
                label = config?.label || "";
                type = config?.type || "";
            }
            label = label ? label : fi.replace(".", ".");
            if (type === "date" && values[fi].key) {
                return (
                    <span key={fi} className="filter">
                        {`${label} : ${moment(values[fi]?.key?.from).format("MM-DD-YYYY hh:mm A")}, ${moment(values[fi].key.to).format(
                            "MM-DD-YYYY hh:mm A"
                        )} [${values[fi].filters}]`}
                    </span>
                );
            } else {
                return values[fi] &&
                    (values[fi].key ||
                        values[fi].filters?.includes("null") ||
                        values[fi].filters?.includes("not_null") ||
                        values[fi]?.filters?.includes("count_gt_1")) ? (
                    <span key={fi} className="filter">
                        {`${label} : ${values[fi].key} [${values[fi].filters}]`}
                    </span>
                ) : null;
            }
        });
    };

    const uniqueDataMap = {};
    let uniqueData = [];
    if (MyfilterName) {
        const combinedFilters = [...FilterNameFormat(MyfilterName), ...getAppliedFiltersList(myFilters)];
        if (myFilters?.top_n && Object.keys(myFilters?.top_n || {}).length === 0) {
            combinedFilters.push({
                name: "Construction Cost",
                value: [" [Top N]"]
            });
        }
        combinedFilters.forEach(item => {
            const isProjectTotalTopN = item.name === "Construction Cost" && Array.isArray(item.value) && item.value.some(v => v.includes("[Top N]"));
            const appliedFilters = getAppliedFiltersList(myFilters)?.filter(e => e.name === item?.name) || [];
            const hasAppliedFilters = appliedFilters?.length > 0 || item?.name === "Projects";
            const names = filterFields?.find(e => e?.label === item?.name)?.paramKey || "";
            const shouldUpdate =
                (item?.value?.length > 0 || uniqueDataMap?.[item?.name]?.value?.length === 0 || myFilters?.[names]?.length === 0) &&
                hasAppliedFilters;

            if (((!uniqueDataMap[item?.name] || isProjectTotalTopN) && hasAppliedFilters) || shouldUpdate) {
                uniqueDataMap[item?.name + (isProjectTotalTopN ? "_TopN" : "")] = isProjectTotalTopN && item?.value?.[0] === " [Top N]" ? "" : item;
            }
        });

        uniqueData = Object.values(uniqueDataMap);
    }
    const appliedFilters = MyfilterName
        ? uniqueData?.filter(item => {
              if (uniqueData?.some(i => i?.name === "FCA Projects" && i?.value?.length)) {
                  return item?.name !== "Projects";
              }
              return true;
          })
        : getAppliedFiltersList(myFilters);
    const renderQueries = () => {
        let filterNames = [];
        if (queries?.length) {
            filterNames.push({ name: "Saved Filters", value: queries?.map(query => query.name) });
            return filterNames;
        }
    };
    const renderLocalQueries = () => {
        let filterNames = [];
        if (localQueries?.length) {
            filterNames.push({ name: "Saved Filters", value: localQueries?.map(query => query.name) });
            return filterNames;
        }
    };
    return (
        <React.Fragment>
            <div className="modal modal-region modal-view modal-applied-filter" id="modalId" style={{ display: "block" }} tabIndex="-1">
                <Draggable handle=".draggable" cancel=".not-draggable">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header draggable">
                                <h5 className="modal-title" id="exampleModalLabel">
                                    <div className="txt-hed">Applied Filters </div>
                                </h5>
                                {showFiltersIcon && (
                                    <button
                                        className={`filter-button blue-common-btn mr-4 cursor-pointer not-draggable`}
                                        data-tip={`View Filters`}
                                        data-for="dashboard-icons-right"
                                        onClick={() => openFiltersPopup()}
                                    >
                                        <img src={FilterIcon} alt="" />
                                        <span> Filters</span>
                                    </button>
                                )}
                                <button type="button" className="close not-draggable" onClick={onCancel}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <div className="modal-body region-otr filter-apply">
                                {source === "dashboard" ? (
                                    <div className="fil-badge-otr">
                                        {source === "recommendation" && <h6>From Dashboard</h6>}
                                        {(!isEmpty(appliedFilters) || !isEmpty(renderQueries())) && (
                                            <div className="tag-ara-filtr">
                                                <div className={"tag-scrol"}>
                                                    <>
                                                        {renderFilters(appliedFilters)}
                                                        {renderFilters(renderQueries())}
                                                    </>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : source === "recommendation" ? (
                                    <>
                                        <div className="fil-badge-otr">
                                            {source === "recommendation" && <h6>From Dashboard</h6>}
                                            {!isEmpty(filterContents) && (
                                                <div className="tag-ara">
                                                    <div className={"tag-scrol"}>{renderFilters(filterContents)}</div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : null}
                                {assetDashboardFilterTags?.length > 0 && (
                                    <div className="fil-badge-otr">
                                        <h6>Dashboard Filter</h6>
                                        <div className="tag-ara">
                                            <div className="tag-scrol">
                                                {assetDashboardFilterTags.map((tag, index) => (
                                                    <span className="filter" key={`asset-dashboard-filter-${index}`}>
                                                        {tag.name}: {Array.isArray(tag.value) ? tag.value.join(", ") : tag.value}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {extraFilterTags?.length > 0 && (
                                    <div className="fil-badge-otr">
                                        {extraFilterTagsAsSection && extraFilterTagsHeading ? <h6>{extraFilterTagsHeading}</h6> : null}
                                        <div className="tag-ara">
                                            <div className="tag-scrol">
                                                {!extraFilterTagsAsSection && extraFilterTagsHeading ? (
                                                    <span className="badge-otr">{extraFilterTagsHeading} </span>
                                                ) : null}
                                                {extraFilterTags.map((tag, index) => (
                                                    <span className="filter" key={`extra-filter-${index}`}>
                                                        {tag.name} : {Array.isArray(tag.value) ? tag.value.join(", ") : tag.value}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {(source === "asset" || source === "recommendation" || source === "asset_survey") && (
                                    <div className="fil-badge-otr">
                                        {(source === "recommendation" || source === "asset" || source === "asset_survey") && <h6>From Local</h6>}
                                        {(!isEmpty(appliedFilters && filterValues) || !isEmpty(renderLocalQueries())) && (
                                            <div className="tag-ara">
                                                <div className={"tag-scrol"}>
                                                    {
                                                        <>
                                                            {renderFilters(appliedFilters)}
                                                            {renderFilters(renderLocalQueries())}
                                                            {filterValues &&
                                                                Object.keys(filterValues).map((f, key) => {
                                                                    if (f === "filters" && filterValues[f] && Object.keys(filterValues[f])?.length)
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">Wild card filter </span>
                                                                                {renderWildCardFilter(filterValues[f])}
                                                                            </>
                                                                        );
                                                                    if (
                                                                        f === "list" &&
                                                                        filterValues[f] &&
                                                                        typeof filterValues[f] === "object" &&
                                                                        Object.entries(filterValues[f]).length !== 0
                                                                    )
                                                                        return (
                                                                            <>
                                                                                {source === "asset" || source === "asset_survey" ? (
                                                                                    renderAssetListFilter(filterValues[f])
                                                                                ) : (
                                                                                    <>
                                                                                        {" "}
                                                                                        <span className="badge-otr">Common filter </span>
                                                                                        {renderListFilter(filterValues[f])}
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    if (f === "search" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">Global search : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "surveyor" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">Surveyor : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "requestor" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">Requestor : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "me" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    Surveyor : {localStorage.getItem("user") ?? ""}{" "}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    if (f === "image_or_not" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    Images Present : {filterValues[f] === "true" ? "Yes" : "No"}{" "}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    if (f === "caption_missing" && typeof filterValues[f] === "boolean")
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    Image(s) Caption Missing : {filterValues[f] ? "Yes" : "No"}{" "}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    if (f === "infrastructure_request" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    {capitalRequestLabel} : {filterValues[f]}{" "}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    if (f === "water" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">Water Present : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "energy" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">Energy Present : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "fmp" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">MFP Present : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "facility_master_plan" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">MFP Present : {filterValues[f]} </span>
                                                                            </>
                                                                        );
                                                                    if (f === "recommendation_type" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    Recommendation Type : {filterValues[f]}{" "}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    if (f === "budget_priority" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    Budget Priority : {filterValues[f]}{" "}
                                                                                </span>
                                                                            </>
                                                                        );

                                                                    if (f === "recommendation_assigned_true" && filterValues[f])
                                                                        return (
                                                                            <>
                                                                                {" "}
                                                                                <span className="badge-otr">
                                                                                    Recommendation Assigned :{" "}
                                                                                    {filterValues[f] === "true" ? "Yes" : "No"}{" "}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                })}
                                                        </>
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        </React.Fragment>
    );
};
